import { Directive, ElementRef, Host, Injector, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import {
  AbstractControl,
  NgControl,
  NG_VALIDATORS,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { compareAsc, format, isValid, parse, parseJSON } from 'date-fns';
import { Calendar } from 'primeng/calendar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from '../../config/app.config';

/**
 * @description 日期元件驗證
 * example
 * <p-calendar
 *  #calendar
 *  [(ngModel)]='date'
 *  #calendarControl='ngModel'
 *  [dateFormat]='appConfig.format.datePrimeng'
 *  [minDate]='2022/1/1'
 *  [maxDate]='2022/3/1'
 *  (onInput)='calendarControl.control.updateValueAndValidity()' // 確保每次輸入都能觸發檢核
 *  (onSelect)='calendarControl.control.updateValueAndValidity()' // 確保每次輸入都能觸發檢核
 *  sigvCalendarValidator
 *  [ele]='calendar'
 * >
 * </p-calendar>
 */
@Directive({
  selector: '[sigvCalendarValidator]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: CalendarValidatorDirective,
      multi: true,
    },
  ],
})
export class CalendarValidatorDirective implements OnInit, OnDestroy, Validator {
  @Input() ele!: any;
  @Input() isRequire = true;

  private readonly destroy$ = new Subject();
  private _control: AbstractControl | null = null;
  isControl = false;

  constructor(
    private injector: Injector,
    private appConfig: AppConfig,
    @Host() @Self() @Optional() private calendar: Calendar,
    private el: ElementRef,
  ) {}

  get control(): AbstractControl | null {
    const ngControlDirective = this.injector.get(NgControl);
    const control = ngControlDirective.control as any | null;
    if (control) {
      this._control = control;
    }

    return this._control;
  }

  ngOnInit(): void {
    // 因中文輸入法輸入問題另行抓值
    if (this.calendar) {
      this.calendar.onInput.pipe(takeUntil(this.destroy$)).subscribe(() => {
        let parsedDate: Date | null;

        try {
          const inputElement = this.el.nativeElement.querySelector('input');
          parsedDate = this.calendar.parseValueFromString(inputElement.value) as Date;
        } catch (e) {
          parsedDate = null;
        }
        if (parsedDate) {
          this.calendar.value = parsedDate;
          this.control?.setValue(parsedDate);
          this.control?.updateValueAndValidity();
        }
      });
    }
  }

  private getInputValue(control: AbstractControl): string[] {
    let inputValue = [] as string[];

    if (control.value) {
      this.isControl = true;
      if (Array.isArray(control.value)){
        inputValue = control.value.map((value) => {
          return value ? format(parseJSON(value), this.appConfig.format.date) : '';
        });
      }else{
        const value = format(parseJSON(control.value), this.appConfig.format.date);
        inputValue.push(value);
      }
    }else{
      this.isControl = false;
      if (this.ele.inputfieldViewChild) {
        if (this.ele.inputfieldViewChild.nativeElement.value){
          inputValue = this.ele.inputfieldViewChild.nativeElement.value.split(' - ');
        }else{
          inputValue.push('');
        }
      }
    }

    return inputValue;
  }

  private checkOut(inputValue: string): ValidationErrors | null {
    const placeholder: string[] = this.ele.placeholder.split(' - ');

    if (inputValue === '' && this.isRequire) {
      return { required: 'Required' };
    }

    const date = this.isControl ?
      parse(inputValue, this.appConfig.format.date, new Date()) :
      parse(inputValue, placeholder[0], new Date());

    if (!isValid(date) && !!inputValue) {
      return { invalidDate: 'InvalidDate' };
    }

    if (this.ele._maxDate) {
      const maxDate = parseJSON(this.ele._maxDate);
      if (compareAsc(date, maxDate) === 1) {
        return { overMaxDate: 'OverMaxDate' };
      }
    }

    if (this.ele._minDate) {
      const minDate = parseJSON(this.ele._minDate);
      if (compareAsc(date, minDate) === -1) {
        return { overMinDate: 'OverMinDate' };
      }
    }

    return null;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const inputValue = this.getInputValue(control);

    let error = null as ValidationErrors | null;
    inputValue.every((value) => {
      const result = this.checkOut(value);

      error = result ? result : null;
      return result === null;
    });

    return error;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
